import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { Edit, Delete, Report } from "@mui/icons-material";
import { togglePublishChapter, delistChapter } from "../utils/api"; // Import delistChapter API

const ChapterListEditorial = ({ storyID, chapters = {}, onDelete, isUGC = false }) => {
  const navigate = useNavigate();
  const [loadingPublishIndex, setLoadingPublishIndex] = useState(null);
  const [loadingDelistIndex, setLoadingDelistIndex] = useState(null);
  const [localChapters, setLocalChapters] = useState(chapters); // Store chapters locally
  const [openDelistDialog, setOpenDelistDialog] = useState(false);
  const [delistReason, setDelistReason] = useState("");
  const [selectedChapter, setSelectedChapter] = useState(null);

  const chaptersArray = Object.entries(localChapters).map(([index, chapter]) => ({
    ...chapter,
    index,
  }));

  const handleTogglePublish = async (chapterIndex, isCurrentlyPublished) => {
    setLoadingPublishIndex(chapterIndex);
    try {
      const response = await togglePublishChapter(storyID, chapterIndex, !isCurrentlyPublished);
      if (response.success) {
        setLocalChapters((prevChapters) => ({
          ...prevChapters,
          [chapterIndex]: {
            ...prevChapters[chapterIndex],
            isPublished: !isCurrentlyPublished,
          },
        }));
      }
    } catch (error) {
      console.error(`Error toggling publish status for chapter ${chapterIndex}:`, error);
      alert(`Failed to update publish status for chapter ${chapterIndex}.`);
    } finally {
      setLoadingPublishIndex(null);
    }
  };

  const handleOpenDelistDialog = (chapterIndex) => {
    setSelectedChapter(chapterIndex);
    setOpenDelistDialog(true);
  };

  const handleCloseDelistDialog = () => {
    setOpenDelistDialog(false);
    setDelistReason("");
    setSelectedChapter(null);
  };

  const handleDelistChapter = async () => {
    if (!delistReason.trim()) {
      alert("Please provide a reason for delisting.");
      return;
    }

    setLoadingDelistIndex(selectedChapter);
    try {
      const response = await delistChapter(storyID, selectedChapter, delistReason);
      if (response.success) {
        setLocalChapters((prevChapters) => ({
          ...prevChapters,
          [selectedChapter]: {
            ...prevChapters[selectedChapter],
            isDelisted: true,
            isPublished: false,
          },
        }));
        alert("Chapter successfully delisted.");
      } else {
        alert("Failed to delist chapter.");
      }
    } catch (error) {
      console.error(`Error delisting chapter ${selectedChapter}:`, error);
      alert("Failed to delist chapter.");
    } finally {
      setLoadingDelistIndex(null);
      handleCloseDelistDialog();
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Chapters
      </Typography>

      <List>
        {chaptersArray.map((chapter, idx) => (
          <React.Fragment key={chapter.index}>
            <ListItem>
              <ListItemText
                primary={
                  isUGC ? (
                    // Make chapter title clickable for editing
                    <Typography
                      variant="body1"
                      component="span"
                      sx={{ cursor: "pointer", textDecoration: "underline" }}
                      onClick={() =>
                        navigate(`/editorial/stories/${storyID}/${chapter.index}`, {
                          state: {
                            contentUrl: chapter.scenarioUrl,
                            title: chapter.title,
                          },
                        })
                      }
                    >
                      {chapter.title || `Chapter ${chapter.index}`}
                    </Typography>
                  ) : (
                    // Make chapter title clickable for editing
                    <Typography
                      variant="body1"
                      component="span"
                      sx={{ cursor: "pointer", textDecoration: "underline" }}
                      onClick={() =>
                        navigate(`/editorial/stories/${storyID}/${chapter.index}`, {
                          state: {
                            contentUrl: chapter.scenarioUrl,
                            title: chapter.title,
                          },
                        })
                      }
                    >
                      {chapter.title || `Chapter ${chapter.index}`}
                    </Typography>
                  )
                }
                secondary={
                  `Updated at: ${chapter.updatedAt
                    ? new Date(chapter.updatedAt._seconds * 1000).toLocaleString()
                    : "Unknown"
                  }` +
                  (chapter.flaggedAt &&
                    chapter.updatedAt &&
                    chapter.flaggedAt._seconds > chapter.updatedAt._seconds
                    ? " - Flagged at: " + new Date(chapter.flaggedAt._seconds * 1000).toLocaleString()
                    : "")
                }
              />

              <ListItemSecondaryAction>
                {isUGC ? null : (
                  <>
                    {/* Allow Delete only for the last chapter */}
                    {parseInt(chapter.index) === chaptersArray.length && (
                      <IconButton sx={{ color: "error.main" }} onClick={() => onDelete(chapter.index)}>
                        <Delete />
                      </IconButton>
                    )}

                    {/* Publish / Unpublish Button */}
                    <Button
                      variant="contained"
                      sx={{
                        ml: 1,
                        backgroundColor: chapter.isPublished ? "red" : "green",
                        color: "#fff",
                      }}
                      onClick={() => handleTogglePublish(chapter.index, chapter.isPublished)}
                      disabled={loadingPublishIndex === chapter.index}
                    >
                      {loadingPublishIndex === chapter.index ? (
                        <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                      ) : null}
                      {chapter.isPublished ? "Unpublish" : "Publish"}
                    </Button>
                  </>
                )}
                {/* Delist Button */}
                {chapter.flaggedAt &&
                  chapter.updatedAt &&
                  chapter.flaggedAt._seconds > chapter.updatedAt._seconds ? (
                  <Button
                    variant="contained"
                    color="secondary"
                    sx={{ ml: 1 }}
                    startIcon={<Report />}
                    onClick={() => handleOpenDelistDialog(chapter.index)}
                    disabled={loadingDelistIndex === chapter.index}
                  >
                    {loadingDelistIndex === chapter.index ? (
                      <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    ) : null}
                    Delist
                  </Button>
                ) : null}
              </ListItemSecondaryAction>
            </ListItem>
            {idx < chaptersArray.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>

      {/* Delist Chapter Dialog */}
      <Dialog open={openDelistDialog} onClose={handleCloseDelistDialog}>
        <DialogTitle>Delist Chapter</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Please provide a reason for delisting this chapter.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={delistReason}
            onChange={(e) => setDelistReason(e.target.value)}
            placeholder="Enter reason for delisting..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelistDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDelistChapter}
            color="error"
            disabled={loadingDelistIndex !== null}
          >
            {loadingDelistIndex !== null ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Confirm Delist
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChapterListEditorial;
